import React, { useState, useEffect } from 'react';
import { getMyAgreements, downloadAgreementPDF, deleteAgreement } from '../services/api';

const COURIER_TRACKING_URLS = {
  'Delhivery': 'https://www.delhivery.com/track/package/',
  'BlueDart': 'https://www.bluedart.com/tracking/',
  'DTDC': 'https://www.dtdc.in/tracking.asp',
  'IndiaPost': 'https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx'
};

const DELIVERY_STAGES = [
  { key: 'order_placed', label: 'Order Placed', icon: '📋' },
  { key: 'printed', label: 'Printed', icon: '🖨️' },
  { key: 'shipped', label: 'Shipped', icon: '📦' },
  { key: 'delivered', label: 'Delivered', icon: '✅' }
];

export default function MyAgreements() {
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all'); // 'all' | 'in_progress' | 'delivered'

  useEffect(() => {
    loadAgreements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadAgreements = async () => {
    setLoading(true);
    try {
      const data = await getMyAgreements(activeTab);
      setAgreements(data.agreements || []);
    } catch (error) {
      console.error('Error loading agreements:', error);
      alert('Failed to load agreements. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (agreement) => {
    try {
      await downloadAgreementPDF(agreement.id, agreement.tenant_name);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download PDF. Please try again.');
    }
  };

  const handleDelete = async (agreementId) => {
    if (!window.confirm('Are you sure you want to delete this agreement? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteAgreement(agreementId);
      loadAgreements(); // Reload list
      alert('Agreement deleted successfully');
    } catch (error) {
      console.error('Delete error:', error);
      alert('Failed to delete agreement. Please try again.');
    }
  };

  const getStatusBadgeClass = (status) => {
    const classes = {
      'order_placed': 'bg-blue-100 text-blue-800',
      'printed': 'bg-yellow-100 text-yellow-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800'
    };
    return classes[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    const labels = {
      'order_placed': 'Order Placed',
      'printed': 'Printed',
      'shipped': 'Shipped',
      'delivered': 'Delivered'
    };
    return labels[status] || status;
  };

  const getCurrentStageIndex = (status) => {
    return DELIVERY_STAGES.findIndex(s => s.key === status);
  };

  const getTrackingUrl = (courier, trackingNumber) => {
    const baseUrl = COURIER_TRACKING_URLS[courier];
    return baseUrl ? `${baseUrl}${trackingNumber}` : null;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '24px' }}>
      <style>{MY_AGREEMENTS_STYLES}</style>

      {/* Header */}
      <div className="ma-header">
        <h1 className="ma-title">My Agreements</h1>
        <p className="ma-subtitle">Track your rental agreement orders</p>
      </div>

      {/* Filter Tabs */}
      <div className="ma-tabs">
        <button
          className={`ma-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          All ({agreements.length})
        </button>
        <button
          className={`ma-tab ${activeTab === 'in_progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('in_progress')}
        >
          In Progress ({agreements.filter(a => ['order_placed', 'printed', 'shipped'].includes(a.delivery_status)).length})
        </button>
        <button
          className={`ma-tab ${activeTab === 'delivered' ? 'active' : ''}`}
          onClick={() => setActiveTab('delivered')}
        >
          Delivered ({agreements.filter(a => a.delivery_status === 'delivered').length})
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="ma-loading">Loading agreements...</div>
      )}

      {/* Empty State */}
      {!loading && agreements.length === 0 && (
        <div className="ma-empty">
          <div className="ma-empty-icon">📄</div>
          <h3>No agreements found</h3>
          <p>Create your first agreement from the "Agreements" tab</p>
        </div>
      )}

      {/* Agreements Grid */}
      {!loading && agreements.length > 0 && (
        <div className="ma-grid">
          {agreements.map((agreement) => {
            const currentStage = getCurrentStageIndex(agreement.delivery_status);
            const trackingUrl = agreement.tracking_number && agreement.courier_company
              ? getTrackingUrl(agreement.courier_company, agreement.tracking_number)
              : null;

            return (
              <div key={agreement.id} className="ma-card">
                {/* Card Header */}
                <div className="ma-card-header">
                  <div>
                    <h3 className="ma-card-title">{agreement.tenant_name}</h3>
                    <p className="ma-card-subtitle">
                      {agreement.building_name} - {agreement.unit_number}
                    </p>
                  </div>
                  <span className={`ma-badge ${getStatusBadgeClass(agreement.delivery_status)}`}>
                    {getStatusLabel(agreement.delivery_status)}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="ma-progress-section">
                  <div className="ma-progress-stages">
                    {DELIVERY_STAGES.map((stage, idx) => {
                      const isActive = idx <= currentStage;
                      const isCompleted = idx < currentStage;
                      
                      return (
                        <div key={stage.key} className="ma-stage">
                          <div className={`ma-stage-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}>
                            {isCompleted ? '✓' : stage.icon}
                          </div>
                          <div className="ma-stage-label">{stage.label}</div>
                          {idx < DELIVERY_STAGES.length - 1 && (
                            <div className={`ma-stage-line ${isCompleted ? 'completed' : ''}`}></div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Details */}
                <div className="ma-details">
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Agreement Type:</span>
                    <span className="ma-detail-value">{agreement.agreement_type === 'rental' ? 'Rental' : 'Lease'}</span>
                  </div>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Move-in Date:</span>
                    <span className="ma-detail-value">{formatDate(agreement.move_in_date)}</span>
                  </div>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Monthly Rent:</span>
                    <span className="ma-detail-value">₹{agreement.monthly_rent.toLocaleString()}</span>
                  </div>
                  <div className="ma-detail-row">
                    <span className="ma-detail-label">Order Date:</span>
                    <span className="ma-detail-value">{formatDate(agreement.ordered_at || agreement.paid_at)}</span>
                  </div>
                  
                  {agreement.tracking_number && (
                    <>
                      <div className="ma-detail-row">
                        <span className="ma-detail-label">Tracking Number:</span>
                        <span className="ma-detail-value tracking-number">{agreement.tracking_number}</span>
                      </div>
                      <div className="ma-detail-row">
                        <span className="ma-detail-label">Courier:</span>
                        <span className="ma-detail-value">{agreement.courier_company}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* Timeline */}
                {(agreement.ordered_at || agreement.printed_at || agreement.shipped_at || agreement.delivered_at) && (
                  <div className="ma-timeline">
                    {agreement.ordered_at && (
                      <div className="ma-timeline-item">
                        <span className="ma-timeline-icon">📋</span>
                        <span className="ma-timeline-text">Ordered: {formatDateTime(agreement.ordered_at)}</span>
                      </div>
                    )}
                    {agreement.printed_at && (
                      <div className="ma-timeline-item">
                        <span className="ma-timeline-icon">🖨️</span>
                        <span className="ma-timeline-text">Printed: {formatDateTime(agreement.printed_at)}</span>
                      </div>
                    )}
                    {agreement.shipped_at && (
                      <div className="ma-timeline-item">
                        <span className="ma-timeline-icon">📦</span>
                        <span className="ma-timeline-text">Shipped: {formatDateTime(agreement.shipped_at)}</span>
                      </div>
                    )}
                    {agreement.delivered_at && (
                      <div className="ma-timeline-item">
                        <span className="ma-timeline-icon">✅</span>
                        <span className="ma-timeline-text">Delivered: {formatDateTime(agreement.delivered_at)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="ma-actions">
                  {agreement.pdf_url && (
                    <button 
                      className="ma-btn ma-btn-primary"
                      onClick={() => handleDownload(agreement)}
                    >
                      📄 Download PDF
                    </button>
                  )}
                  
                  {trackingUrl && (
                    <a 
                      href={trackingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ma-btn ma-btn-secondary"
                    >
                      📍 Track Courier
                    </a>
                  )}
                  
                  <button 
                    className="ma-btn ma-btn-danger"
                    onClick={() => handleDelete(agreement.id)}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const MY_AGREEMENTS_STYLES = `
  .ma-header {
    margin-bottom: 24px;
  }
  .ma-title {
    font-size: 24px;
    font-weight: 700;
    color: #1f4e79;
    margin: 0 0 4px 0;
  }
  .ma-subtitle {
    font-size: 14px;
    color: #64748b;
    margin: 0;
  }

  /* Tabs */
  .ma-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    border-bottom: 2px solid #e2e8f0;
  }
  .ma-tab {
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 500;
    color: #64748b;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    transition: all 0.2s;
  }
  .ma-tab:hover {
    color: #1f4e79;
  }
  .ma-tab.active {
    color: #1f4e79;
    border-bottom-color: #f26522;
  }

  /* Loading & Empty */
  .ma-loading {
    text-align: center;
    padding: 60px 20px;
    color: #64748b;
    font-size: 16px;
  }
  .ma-empty {
    text-align: center;
    padding: 80px 20px;
  }
  .ma-empty-icon {
    font-size: 64px;
    margin-bottom: 16px;
  }
  .ma-empty h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1f4e79;
    margin: 0 0 8px 0;
  }
  .ma-empty p {
    font-size: 14px;
    color: #64748b;
  }

  /* Grid */
  .ma-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 24px;
  }

  /* Card */
  .ma-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(31, 78, 121, 0.08);
    transition: box-shadow 0.2s;
  }
  .ma-card:hover {
    box-shadow: 0 4px 16px rgba(31, 78, 121, 0.12);
  }

  .ma-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }
  .ma-card-title {
    font-size: 18px;
    font-weight: 700;
    color: #1f4e79;
    margin: 0 0 4px 0;
  }
  .ma-card-subtitle {
    font-size: 13px;
    color: #64748b;
    margin: 0;
  }

  /* Badge */
  .ma-badge {
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }
  .bg-blue-100 { background: #dbeafe; }
  .text-blue-800 { color: #1e40af; }
  .bg-yellow-100 { background: #fef3c7; }
  .text-yellow-800 { color: #92400e; }
  .bg-purple-100 { background: #e9d5ff; }
  .text-purple-800 { color: #6b21a8; }
  .bg-green-100 { background: #d1fae5; }
  .text-green-800 { color: #065f46; }

  /* Progress Section */
  .ma-progress-section {
    margin: 20px 0;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
  }
  .ma-progress-stages {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .ma-stage {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
  }
  .ma-stage-dot {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #94a3b8;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    margin-bottom: 8px;
    z-index: 2;
    transition: all 0.3s;
  }
  .ma-stage-dot.active {
    background: #f26522;
    color: white;
  }
  .ma-stage-dot.completed {
    background: #10b981;
    color: white;
  }
  .ma-stage-label {
    font-size: 11px;
    font-weight: 500;
    color: #64748b;
    text-align: center;
  }
  .ma-stage-line {
    position: absolute;
    top: 20px;
    left: 50%;
    width: 100%;
    height: 2px;
    background: #e2e8f0;
    z-index: 1;
  }
  .ma-stage-line.completed {
    background: #10b981;
  }

  /* Details */
  .ma-details {
    margin: 16px 0;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
  }
  .ma-detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  .ma-detail-row:last-child {
    margin-bottom: 0;
  }
  .ma-detail-label {
    font-size: 12px;
    color: #64748b;
  }
  .ma-detail-value {
    font-size: 12px;
    font-weight: 600;
    color: #1f4e79;
  }
  .tracking-number {
    font-family: monospace;
    background: #e2e8f0;
    padding: 2px 8px;
    border-radius: 4px;
  }

  /* Timeline */
  .ma-timeline {
    margin: 16px 0;
    padding: 12px;
    border-left: 2px solid #e2e8f0;
  }
  .ma-timeline-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    padding-left: 12px;
  }
  .ma-timeline-item:last-child {
    margin-bottom: 0;
  }
  .ma-timeline-icon {
    font-size: 16px;
  }
  .ma-timeline-text {
    font-size: 12px;
    color: #64748b;
  }

  /* Actions */
  .ma-actions {
    display: flex;
    gap: 8px;
    margin-top: 16px;
  }
  .ma-btn {
    flex: 1;
    padding: 10px 16px;
    font-size: 13px;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-block;
    text-align: center;
  }
  .ma-btn-primary {
    background: #1f4e79;
    color: white;
  }
  .ma-btn-primary:hover {
    background: #163a5c;
  }
  .ma-btn-secondary {
    background: #f26522;
    color: white;
  }
  .ma-btn-secondary:hover {
    background: #d94e0d;
  }
  .ma-btn-danger {
    background: #ef4444;
    color: white;
  }
  .ma-btn-danger:hover {
    background: #dc2626;
  }

  /* Responsive */
  @media (max-width: 768px) {
    .ma-grid {
      grid-template-columns: 1fr;
    }
    .ma-actions {
      flex-direction: column;
    }
  }
`;